import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./RatingSection.css";
import { showErrorToast, showSuccessToast } from "../../../utils/toast";
import { getErrorMessage } from "../../../constants/globalFunction";
import { appApiHandler } from "../../../api/appApiHandler";
import { string } from "../../../constants/string";

const RatingSection = ({ product, onReviewSubmit }) => {
  const { userData } = useSelector((state) => state.userState);
  const navigate = useNavigate();
  const [userRating, setUserRating] = useState(0);
  const [reviewContent, setReviewContent] = useState('');
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);

  const handleSubmitReview = async () => {
    try {
      if (userData) {
        setReviewLoading(true);
        const reqData = {
          product_id: product?._id,
          user_id: userData?._id,
          rating: userRating,
          review: reviewContent,
        };

        const response = await appApiHandler.addReviewRating(reqData);
        if (response?.status === 200) {
          onReviewSubmit();
          setReviewContent('');
          setUserRating(0);
          setShowReviewForm(false);
          showSuccessToast("Review submitted successfully");
        } else if (response?.message) {
          showErrorToast(getErrorMessage(response?.message));
        } else {
          showErrorToast(string.error.failed_due_to_internal_error);
        }
      } else {
        navigate('/login');
      }
    } catch (error) {
      showErrorToast(string.error.failed_due_to_internal_error);
      console.log('Error submitting review:', error);
    }
    finally {
      setReviewLoading(false);
    }
  };

  const toggleReviewForm = () => {
    if (!userData) {
      navigate('/login');
      return;
    }
    setShowReviewForm(!showReviewForm);
    if (showReviewForm) {
      setReviewContent('');
      setUserRating(0);
    }
  };

  return (
    <div className="product-rating-section">
      <h2 style={{
        textAlign: 'center',
        color: 'var(--color-black)'
      }}>Customer Reviews</h2>
      <div className="product-rating-content">
        {/* Single Row Layout */}
        <div className="product-rating-row">
          {
            (product?.total_ratings > 0) ? (
              <div className="product-rating-average">
                <div style={{
                  alignItems: 'center'
                }}>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div className="product-rating-stars-display">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span
                          key={star}
                          className={`product-rating-display-star active`}
                        >
                          ★
                        </span>
                      ))}
                    </div>
                    <span className="product-rating-out-of">{product?.average_rating || 0}&nbsp;out of 5</span>
                  </div>
                  <div className="product-rating-info">
                    <span className="product-review-count">Based on {product?.total_ratings || 0} reviews</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="product-rating-average">
                <div style={{
                  alignItems: 'center'
                }}>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div className="product-rating-stars-display">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span
                          key={star}
                          className={`product-rating-display-star active`}
                        >
                          ★
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="product-rating-info">
                    <span className="product-review-count">Be the first to write a review</span>
                  </div>
                </div>
              </div>
            )
          }

          {
            product?.total_ratings > 0 && (
              <div className={`product-rating-bars-container ${!userData || (userData && product?.rating_given > 0) ? 'aligned-start' : ''}`}>
                <div className="product-rating-bar">
                  <div className="product-rating-star-label">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span key={star} className={`product-rating-label-star ${star <= 5 ? "filled" : "unfilled"}`}>★</span>
                    ))}
                  </div>
                  <div className="product-rating-progress">
                    <div
                      className="product-rating-fill"
                      style={{ width: `${(product?.rating_distribution?.['4_1_to_5'] || 0) * 100}%` }}
                    />
                  </div>
                  <span className="product-rating-count">{product?.rating_distribution?.['4_1_to_5'] || 0}</span>
                </div>
                <div className="product-rating-bar">
                  <div className="product-rating-star-label">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span key={star} className={`product-rating-label-star ${star <= 4 ? "filled" : "unfilled"}`}>★</span>
                    ))}
                  </div>
                  <div className="product-rating-progress">
                    <div
                      className="product-rating-fill"
                      style={{ width: `${(product?.rating_distribution?.['3_1_to_4'] || 0) * 100}%` }}
                    />
                  </div>
                  <span className="product-rating-count">{product?.rating_distribution?.['3_1_to_4'] || 0}</span>
                </div>
                <div className="product-rating-bar">
                  <div className="product-rating-star-label">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span key={star} className={`product-rating-label-star ${star <= 3 ? "filled" : "unfilled"}`}>★</span>
                    ))}
                  </div>
                  <div className="product-rating-progress">
                    <div
                      className="product-rating-fill"
                      style={{ width: `${(product?.rating_distribution?.['2_1_to_3'] || 0) * 100}%` }}
                    />
                  </div>
                  <span className="product-rating-count">{product?.rating_distribution?.['2_1_to_3'] || 0}</span>
                </div>
                <div className="product-rating-bar">
                  <div className="product-rating-star-label">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span key={star} className={`product-rating-label-star ${star <= 2 ? "filled" : "unfilled"}`}>★</span>
                    ))}
                  </div>
                  <div className="product-rating-progress">
                    <div
                      className="product-rating-fill"
                      style={{ width: `${(product?.rating_distribution?.['1_1_to_2'] || 0) * 100}%` }}
                    />
                  </div>
                  <span className="product-rating-count">{product?.rating_distribution?.['1_1_to_2'] || 0}</span>
                </div>
                <div className="product-rating-bar">
                  <div className="product-rating-star-label">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span key={star} className={`product-rating-label-star ${star <= 1 ? "filled" : "unfilled"}`}>★</span>
                    ))}
                  </div>
                  <div className="product-rating-progress">
                    <div
                      className="product-rating-fill"
                      style={{ width: `${(product?.rating_distribution?.['0_to_1'] || 0) * 100}%` }}
                    />
                  </div>
                  <span className="product-rating-count">{product?.rating_distribution?.['0_to_1'] || 0}</span>
                </div>
              </div>
            )
          }

          {
            ((userData && product?.rating_given == 0) || !userData) && (
              <div className="write-review-button-container">
                <button
                  className="product-review-toggle-btn"
                  onClick={toggleReviewForm}
                >
                  {showReviewForm ? 'Cancel Review' : 'Write Review'}
                </button>
              </div>
            )
          }

        </div>

        {/* Review Form - Show/Hide based on state */}
        {showReviewForm && (
          <div className="product-review-form-section">
            <div className="product-review-form-header">
              <h4>Write a Review</h4>
            </div>
            <div className="product-review-form-content">
              <div className="product-review-form-row">
                <label className="product-review-form-label">Rating</label>
                <div className="product-rating-stars-input">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className={`product-rating-star ${star <= (userRating || 0) ? "active" : ""}`}
                      onClick={() => setUserRating(star)}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>
              <div className="product-review-form-row">
                <label className="product-review-form-label">Review Content</label>
                <textarea
                  className="product-review-textarea"
                  placeholder="Start writing here..."
                  value={reviewContent}
                  onChange={(e) => setReviewContent(e.target.value)}
                  rows={4}
                />
              </div>
              <div className="product-review-form-actions">
                <button
                  className="product-review-cancel-btn"
                  onClick={toggleReviewForm}
                  disabled={reviewLoading}
                >
                  Cancel Review
                </button>
                <button
                  className="product-review-submit-btn"
                  onClick={handleSubmitReview}
                  disabled={reviewLoading}
                >
                  {reviewLoading ? 'Submitting...' : 'Submit Review'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RatingSection;
